import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useRef,
} from 'react'
import { useHistory } from 'react-router-dom'

import IFindFavorecidosContaBancariaResponse from '../domain/entities/IFindFavorecidosContaBancariaResponse'
import PagamentoRoutes from '../utils/PagamentoRoutes'
import IContaPagamentoContraCteResponseDTO from '../domain/entities/IContaPagamentoContraCteResponseDTO'

interface ITranferenciaContextState {
  tipoTransferencia?: string
  protocolo?: string
  setProtocolo?: (value: string) => void
  nomeFavorecido?: string
  senha?: string
  setSenha?: (value: string) => void
  setNomeFavorecido?: (value: string) => void
  setTipoTransferencia: (value: string) => void
  valor: number
  setValor: (value: number) => void
  cpf: string
  setCpf: (value: string) => void
  step: number
  setStep: (value: number) => void
  numberOfSteps: number
  handleNextButton: () => void
  handleBackButton: () => void
  showNovaConta: boolean
  setShowNovaConta: (value: boolean) => void
  contaFavorecido: IFindFavorecidosContaBancariaResponse
  setContaFavorecido: (value: IFindFavorecidosContaBancariaResponse) => void
  cnpjFavorecido: string
  setCnpjFavorecido: (value: string) => void
  clear: () => void
  salvarContaFavorecido: boolean
  setSalvarContaFavorecido: (value: boolean) => void
  pin?: React.MutableRefObject<string>
  token?: React.MutableRefObject<string>
  contaPagamento: IContaPagamentoContraCteResponseDTO
  setContaPagamento: (value: IContaPagamentoContraCteResponseDTO) => void
}

export const TransferenciaContext = createContext<ITranferenciaContextState>(
  {} as ITranferenciaContextState,
)

export function useTransferencia(): ITranferenciaContextState {
  const context = useContext(TransferenciaContext)

  if (!context) {
    throw new Error('useTransferencia must be used within an CaminhaoProvider')
  }

  return context
}

export const TransferenciaProvider: React.FC = ({ children }) => {
  const history = useHistory()
  const [step, setStep] = useState(0)
  const [senha, setSenha] = useState(null)
  const [protocolo, setProtocolo] = useState(null)
  const [tipoTransferencia, setTipoTranferencia] = useState(null)
  const [nomeFavorecido, setNomeFavorecido] = useState(null)
  const [valor, setValor] = useState(0)
  const [cpf, setCpf] = useState('')
  const [showNovaConta, setShowNovaConta] = useState(false)
  const [contaFavorecido, setContaFavorecido] =
    useState<IFindFavorecidosContaBancariaResponse>()
  const [cnpjFavorecido, setCnpjFavorecido] = useState('')
  const [salvarContaFavorecido, setSalvarContaFavorecido] = useState<
    boolean | undefined
  >()
  const pin = useRef('')
  const token = useRef('')
  const [contaPagamento, setContaPagamento] =
    useState<IContaPagamentoContraCteResponseDTO>(null)

  const setTipo = useCallback((value: string) => {
    setTipoTranferencia(value)
  }, [])

  const setProtocoloValue = useCallback((value: string) => {
    setProtocolo(value)
  }, [])

  const setNomeFavorecidoValue = useCallback((value: string) => {
    setNomeFavorecido(value)
  }, [])

  const setCpfValue = useCallback((value: string) => {
    setCpf(value)
  }, [])

  const setSenhaValue = useCallback((value: string) => {
    setSenha(value)
  }, [])

  const setStepValue = (value: number) => {
    setStep(value)
  }

  const setValorValue = useCallback((value: number) => {
    setValor(value)
  }, [])

  const setShowNovaContaValue = useCallback((value: boolean) => {
    setShowNovaConta(value)
  }, [])

  const setContaFavorecidoValue = useCallback(
    (value: IFindFavorecidosContaBancariaResponse) => {
      setContaFavorecido(value)
    },
    [],
  )

  const setCnpjFavorecidoValue = useCallback((value: string) => {
    setCnpjFavorecido(value)
  }, [])

  const setContaPagamentoValue = useCallback(
    (value: IContaPagamentoContraCteResponseDTO) => {
      setContaPagamento(value)
    },
    [],
  )

  const numberOfSteps =
    Object.keys({
      TipoTranferencia: 0,
    }).length - 1

  const handleNextButtonValue = () => {
    setStep(step => {
      const newStep = step + 1

      if (newStep > numberOfSteps) {
        return step
      }

      return newStep
    })
  }

  const clear = () => {
    setTipoTranferencia(null)
    setStep(0)
    setSenha(null)
    setProtocolo(null)
    setNomeFavorecido(null)
    setValor(null)
    setCpf(null)
    setContaFavorecido(undefined)
    setContaPagamentoValue(null)
  }

  const handleBackButtonValue = () => {
    setStep(step => {
      const newStep = step - 1

      if (newStep < 0) {
        clear()
        history.push(PagamentoRoutes.PAGAMENTO, { reloadHomePagamento: false })
        return 0
      }

      return step - 1
    })
  }

  return (
    <TransferenciaContext.Provider
      value={{
        protocolo,
        setProtocolo: setProtocoloValue,
        step,
        setStep: setStepValue,
        senha,
        setSenha: setSenhaValue,
        nomeFavorecido,
        setNomeFavorecido: setNomeFavorecidoValue,
        valor,
        setValor: setValorValue,
        cpf,
        setCpf: setCpfValue,
        numberOfSteps,
        handleNextButton: handleNextButtonValue,
        handleBackButton: handleBackButtonValue,
        tipoTransferencia,
        setTipoTransferencia: setTipo,
        showNovaConta,
        setShowNovaConta: setShowNovaContaValue,
        contaFavorecido,
        setContaFavorecido: setContaFavorecidoValue,
        cnpjFavorecido,
        setCnpjFavorecido: setCnpjFavorecidoValue,
        clear,
        salvarContaFavorecido,
        setSalvarContaFavorecido,
        pin,
        token,
        contaPagamento,
        setContaPagamento: setContaPagamentoValue,
      }}
    >
      {children}
    </TransferenciaContext.Provider>
  )
}
